import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useParamFunctions } from "hooks";
import {
  APPLICANT_LIST_KEYWORD_SEARCH_SORT,
  APPLICANT_LIST_SORTING_OPTIONS,
  APPLICANT_TYPES,
  DIRECTION,
  getAllowedSortOptions,
} from "constants/index";
import { deleteEmptyKeys, isEmpty } from "utils";

const KEYS = {
  filters: "f",
  advancedFilters: "af",
  missionId: "missionId",
  missionRoleIds: "missionRoleIds",
  organizationId: "organizationId",
  keyword: "keyword",
  isMember: "isMember",
  applicantState: "applicantState",
  page: "page",
  tab: "tab",
  sorts: "s",
  sortKey: "sortKey",
  sortDirection: "sortDirection",
};

export default function useApplicantListControls({ defaultSelected, initialProgressMenu, initialTab }) {
  const { paramParse, setParams } = useParamFunctions();
  const location = useLocation();
  const locState = location.state;

  const filters = paramParse(KEYS.filters);
  const sorts = paramParse(KEYS.sorts);
  const advancedFilters = paramParse(KEYS.advancedFilters);

  const [selectedProgressMenu, setSelectedProgressMenu] = useState(String(filters?.[KEYS.tab] ?? initialProgressMenu));
  const [selectedTab, setSelectedTab] = useState(initialTab || filters?.[KEYS.page] || 0);
  const prevSelectedRef = useRef({});

  const [selected, setSelected] = useState({
    missionId: filters?.[KEYS.missionId] ?? locState?.missionId ?? defaultSelected?.missionId,
    missionRoleIds: filters?.[KEYS.missionRoleIds] ?? locState?.missionRoleIds ?? defaultSelected?.missionRoleIds,
    organizationId: filters?.[KEYS.organizationId] ?? locState?.organizationId ?? defaultSelected?.organizationId,
    keyword: filters?.[KEYS.keyword] ?? locState?.keyword ?? defaultSelected?.keyword,
    isMember: filters?.[KEYS.isMember] ?? locState?.isMember ?? defaultSelected?.isMember,
    applicantState: filters?.[KEYS.applicantState] ?? defaultSelected?.applicantState ?? APPLICANT_TYPES.applied,
    sortKey: sorts?.[KEYS.sortKey] ?? APPLICANT_LIST_SORTING_OPTIONS[0]?.value,
    sortDirection: sorts?.[KEYS.sortDirection] ?? DIRECTION.descending,
    advancedFilters,
  });

  const hasKeywordSearch = Boolean(selected?.keyword?.trim());
  const allowedSortOptions = useMemo(
    () => getAllowedSortOptions(APPLICANT_LIST_SORTING_OPTIONS, selected?.applicantState),
    [selected?.applicantState]
  );
  const sortingOptions = useMemo(
    () =>
      hasKeywordSearch
        ? allowedSortOptions
        : allowedSortOptions.filter(({ value }) => value !== APPLICANT_LIST_KEYWORD_SEARCH_SORT.sortKey),
    [allowedSortOptions, hasKeywordSearch]
  );
  const resolvedSort = hasKeywordSearch
    ? APPLICANT_LIST_KEYWORD_SEARCH_SORT
    : {
        sortKey: selected?.sortKey,
        sortDirection: selected?.sortDirection,
      };
  const displaySelected = hasKeywordSearch ? { ...selected, ...resolvedSort } : selected;

  /**
   * @description Update the URL params when data changes
   */
  useEffect(() => {
    if (JSON.stringify(deleteEmptyKeys(prevSelectedRef.current)) === JSON.stringify(deleteEmptyKeys(selected))) return;

    setParams(
      KEYS.filters,
      {
        [KEYS.missionId]: selected?.missionId,
        [KEYS.missionRoleIds]: selected?.missionRoleIds,
        [KEYS.organizationId]: selected?.organizationId,
        [KEYS.keyword]: selected?.keyword,
        [KEYS.isMember]: selected?.isMember,
        [KEYS.tab]: selectedProgressMenu,
        [KEYS.applicantState]: selected?.applicantState,
        [KEYS.page]: selectedTab,
      },
      true,
      true
    );

    setParams(
      KEYS.sorts,
      {
        [KEYS.sortKey]: selected?.sortKey,
        [KEYS.sortDirection]: selected?.sortDirection,
      },
      true,
      true
    );

    setParams(KEYS.advancedFilters, selected?.advancedFilters, true, true);

    if (!isEmpty(prevSelectedRef.current)) {
      setParams("page", 1, false);
    }

    prevSelectedRef.current = selected;
  }, [selected, selectedProgressMenu, selectedTab, setParams]);

  const updateApplicantState = (newState) => {
    setSelected((prev) => {
      if (prev.applicantState === newState) return prev;

      const updates = { applicantState: newState };
      const nextAllowedSortOptions = getAllowedSortOptions(APPLICANT_LIST_SORTING_OPTIONS, newState);
      const isSortKeyAllowed = nextAllowedSortOptions.some((option) => option.value === prev.sortKey);

      if (!isSortKeyAllowed) {
        updates.sortKey = nextAllowedSortOptions[0]?.value || APPLICANT_LIST_SORTING_OPTIONS[0]?.value;
      }

      return { ...prev, ...updates };
    });
  };

  return {
    filters,
    selected,
    setSelected,
    displaySelected,
    sortingOptions,
    hasKeywordSearch,
    resolvedSort,
    selectedProgressMenu,
    setSelectedProgressMenu,
    selectedTab,
    setSelectedTab,
    updateApplicantState,
  };
}

export { KEYS };
